import classNames from "classnames";
import React, { memo } from "react";
import Modal from "react-modal";
import { Button, LoadingAnimation, ModalHeader } from "components/Shared";

// ----------------------------------------------------------------------------
// Replace Form confirmation — same orange "warning" shell as DeleteModal so the
// two consequential actions on this page look alike.
//
// This modal only CONFIRMS. Confirming closes it and opens the file picker the
// page owns; the upload and the PUT /forms/:id/replace happen there. Keeping the
// file input in the page means the modal has no request of its own to manage,
// unlike DeleteModal which owns its Datasource.
// ----------------------------------------------------------------------------

interface ReplaceModalProps {
	isOpen: boolean;
	onRequestClose: () => void;
	onConfirm: () => void;
	// True from the moment a file is chosen until the upload and the PUT have
	// both finished. The modal stays open throughout so the spinner has
	// somewhere to live and the person can see the work is still going.
	loading?: boolean;
}

const ReplaceModal = ({ isOpen, onRequestClose, onConfirm, loading = false }: ReplaceModalProps) => (
	<Modal
		ariaHideApp={false}
		isOpen={isOpen}
		className={classNames("warning", "replace-form-modal")}
		// Not dismissable mid-upload: closing would strand a request that is
		// about to change the document behind every open condition.
		onRequestClose={loading ? undefined : onRequestClose}
		contentLabel="replace-form"
	>
		<ModalHeader title="replace_form" onRequestClose={onRequestClose} />
		<section>
			<p>
				The form keeps its name and code. Templates and any form condition that hasn't generated a PDF yet will use
				the new document; conditions already generated keep theirs.
			</p>
		</section>
		<footer>
			{/* Button translates `children` itself — pass the raw i18n key, not a
			    pre-translated string (same as DeleteModal).

			    `cancel-action` is what the stylesheet hooks to grey this one out.
			    Neither button passes a `kind`, so both resolve to `.btn-ok` and the
			    class is the only thing telling them apart — DeleteModal gets away
			    without one because its action button is `kind="destroy"`. */}
			<Button className="cancel-action" onClick={onRequestClose} disabled={loading}>
				cancel
			</Button>
			{/* Same in-flight treatment as DeleteModal: the button keeps its size,
			    position AND its orange fill; the "Replace" label stays but goes
			    transparent so it still reserves the width, and the loader is overlaid
			    absolutely on top so it can never resize the button.

			    LoadingAnimation is the shared ~200px loader, hence the scale — and its
			    arcs are recoloured white in _modal.scss so they read on the orange. */}
			<span className="replace-action" style={{ position: "relative", display: "inline-flex" }}>
				<Button onClick={onConfirm} loading={loading}>
					replace
				</Button>
				{loading && (
					<span
						className="loading replace-spinner"
						aria-hidden="true"
						style={{
							position: "absolute",
							top: "50%",
							left: "50%",
							transform: "translate(-50%, -50%) scale(0.24)",
							padding: 0,
							margin: 0,
							maxHeight: "none",
							pointerEvents: "none",
						}}
					>
						<LoadingAnimation />
					</span>
				)}
			</span>
		</footer>
	</Modal>
);

export default memo(ReplaceModal);
